import {
  CompanyEditFormSchema,
  type CompanyEditFormSchemaType,
} from "@/pages/company-edit/model/schema";
import { BaseButton } from "@/shared/ui/BaseButton";
import { BaseFormLabel } from "@/shared/ui/base-form-label";
import { BaseSelect } from "@/shared/ui/base-select";
import { BaseTextField } from "@/shared/ui/base-text-field";
import { zodResolver } from "@hookform/resolvers/zod";
import { MenuItem, Stack } from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

type Props = {
  initialValues: CompanyEditFormSchemaType;
  onSubmit: (data: CompanyEditFormSchemaType) => void;
  onDelete: () => void;

  disableCompanyName?: boolean;
  hideCompanyCategories?: boolean;
  hideDeleteButton: boolean;
};

export const CompanyEditForm = ({
  initialValues,
  onSubmit,
  onDelete,
  disableCompanyName,
  hideCompanyCategories,
  hideDeleteButton,
}: Props) => {
  const { t } = useTranslation();

  const form = useForm<CompanyEditFormSchemaType>({
    resolver: zodResolver(CompanyEditFormSchema),
    defaultValues: initialValues,
  });

  const onSubmitHandler = (data: CompanyEditFormSchemaType) => {
    onSubmit(data);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmitHandler)}>
      <Stack>
        <Stack spacing={4} flexWrap="wrap" sx={{ maxWidth: "600px" }}>
          <BaseFormLabel label="会社名">
            <Controller
              name="companyName"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="会社名を入力してください"
                  size="small"
                  error={fieldState.invalid}
                  disabled={disableCompanyName}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="郵便番号">
            <Controller
              name="zipCode"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="郵便番号を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="住所">
            <Controller
              name="address"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="住所を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="電話番号">
            <Controller
              name="phoneNumber"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="電話番号を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="FAX番号">
            <Controller
              name="faxNumber"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="FAX番号を入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          <BaseFormLabel label="メールアドレス">
            <Controller
              name="email"
              control={form.control}
              render={({ field, fieldState }) => (
                <BaseTextField
                  {...field}
                  placeholder="メールアドレスを入力してください"
                  size="small"
                  error={fieldState.invalid}
                />
              )}
            />
          </BaseFormLabel>

          {!hideCompanyCategories && (
            <BaseFormLabel label="企業種別">
              <Controller
                name="category"
                control={form.control}
                render={({ field, fieldState }) => (
                  <BaseSelect
                    type="string"
                    value={field.value}
                    onChange={field.onChange}
                    error={fieldState.invalid}
                    size="small"
                  >
                    <MenuItem value="shipper">{t("shipper")}</MenuItem>
                    <MenuItem value="carrier">{t("carrier")}</MenuItem>
                  </BaseSelect>
                )}
              />
            </BaseFormLabel>
          )}
        </Stack>

        {/* ボタン類 */}
        <Stack
          direction="column"
          alignItems="center"
          justifyContent="center"
          spacing={8}
          mt={8}
        >
          <BaseButton variant="contained" type="submit">
            更新
          </BaseButton>

          {!hideDeleteButton && (
            <BaseButton
              variant="contained"
              color="error"
              type="button"
              onClick={onDelete}
            >
              削除
            </BaseButton>
          )}
        </Stack>
      </Stack>
    </form>
  );
};
